import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TvIcon from '@mui/icons-material/Tv';
import {
	Box,
	Chip,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';
import moment from 'moment';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	ArrayField,
	AutocompleteArrayInput,
	AutocompleteInput,
	ChipField,
	Datagrid,
	DateField,
	FunctionField,
	NumberField,
	RecordContextProvider,
	ReferenceArrayField,
	ReferenceArrayInput,
	ReferenceField,
	ReferenceInput,
	SelectArrayInput,
	SelectField,
	SelectInput,
	SingleFieldList,
	TextField,
	TopToolbar,
	useListContext,
	useRecordContext,
	useTranslate,
} from 'react-admin';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import { CustomExportButton, CustomizableDatagrid, PrintButton } from '../../components';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import { filterToQuery, formatAttendantLabel } from '../../utils';

const TV_REFRESH_INTERVAL = 30;

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold',
};

const discountChoices = [
	{ id: 'withDiscount', name: 'Com Desconto' },
	{ id: 'withoutDiscount', name: 'Sem Desconto' },
];

const statusChoices = [
	{ id: 'pendingReserve', name: 'Reserva Pendente' },
	{ id: 'reserved', name: 'Reservado' },
	{ id: 'pendingAuthorization', name: 'Autorização Pendente' },
	{ id: 'authorized', name: 'Autorizado' },
	{ id: 'waiting', name: 'Aguardando Abastecimento' },
	{ id: 'delivering', name: 'Abastecendo' },
	{ id: 'deliveringWithoutFollowUp', name: 'Abastecendo' },
	{ id: 'error', name: 'Erro' },
];

const TV_STATUS_COLORS = {
	pendingReserve: '#FFB300',
	reserved: '#42A5F5',
	pendingAuthorization: '#CE93D8',
	authorized: '#66BB6A',
	waiting: '#26C6DA',
	delivering: '#AED581',
	deliveringWithoutFollowUp: '#AED581',
	error: '#EF5350',
};

// ─── TV Mode Context ──────────────────────────────────────────
const TvModeContext = createContext({
	isTvMode: false,
	countdown: TV_REFRESH_INTERVAL,
	tick: 0,
	refreshTrigger: 0,
	onExit: () => {},
});

// ─── Helpers ─────────────────────────────────────────────────
const formatElapsed = (isoString) => {
	if (!isoString) return '-';
	const secs = Math.floor((Date.now() - new Date(isoString)) / 1000);
	if (secs < 60) return `${secs}s`;
	if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
	return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

const getTvRowBg = (record) => {
	if (record.status === 'error') return 'rgba(239, 83, 80, 0.20)';
	if (!record.reservedAt) return 'rgba(250, 227, 142, 0.18)';
	if (((new Date(record.reservedAt) - new Date(record.startedAt)) / 1000) > 10)
		return 'rgba(250, 152, 127, 0.22)';
	if (record.updatedAt && ((Date.now() - new Date(record.updatedAt)) / 1000) > 180)
		return 'rgba(250, 227, 142, 0.15)';
	return 'transparent';
};

// ─── Field components (unchanged) ────────────────────────────
const ValueField = () => {
	const record = useRecordContext();
	const source = record.value ? 'value' : 'fillInInfo.value';
	return (
		<NumberField
			source={source}
			label="Valor"
			textAlign="center"
			emptyText="R$ 0,000"
			options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
		/>
	);
};

const VolumeField = () => {
	const record = useRecordContext();
	const source = record.volume ? 'volume' : 'fillInInfo.volume';
	return (
		<NumberField
			source={source}
			label="Litros"
			textAlign="center"
			emptyText="0"
			options={{ maximumFractionDigits: 2 }}
		/>
	);
};

const PriceField = () => {
	const record = useRecordContext();
	const source = record.price ? 'price' : 'fillInInfo.price';
	return (
		<NumberField
			source={source}
			label="Preço"
			textAlign="center"
			emptyText="R$ 0,000"
			options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 3, maximumFractionDigits: 3 }}
		/>
	);
};

const FuelField = () => {
	const record = useRecordContext();
	if (!record) return <></>;
	if (record.fuelName) return <TextField source="fuelName" label="Combustível" />;
	const source = record.fuel && record.fuel.id ? 'fuel.id' : 'stationFuel.fuelId';
	return (
		<ReferenceField label="Combustível" emptyText="Não identificado" source={source} reference="fuels" basePath="fuels" link="show">
			<TextField source="name" label="Combustível" />
		</ReferenceField>
	);
};

const slowReserve = (record) => ({
	backgroundColor: !record.reservedAt
		? '#fae38e'
		: ((new Date(record.reservedAt) - new Date(record.startedAt)) / 1000) > 10
		? '#fa987f'
		: (record.updatedAt && ((new Date() - new Date(record.updatedAt)) / 1000) > 180)
		? '#fae38e'
		: '#ffffff',
});

// ─── TvHeader ─────────────────────────────────────────────────
const TvHeader = ({ onExit, countdown }) => {
	const [now, setNow] = useState(new Date());

	useEffect(() => {
		const t = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(t);
	}, []);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				px: 2.5,
				py: 1,
				background: 'linear-gradient(90deg, #0d0f18 0%, #1a1d2e 100%)',
				borderBottom: '1px solid rgba(255,255,255,0.07)',
				flexShrink: 0,
			}}
		>
			{/* Left: title + date */}
			<Box>
				<Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
					Abastecimentos em Andamento
				</Typography>
				<Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem', textTransform: 'capitalize' }}>
					{now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
				</Typography>
			</Box>

			{/* Legend */}
			<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
				{[
					{ color: '#EF5350', label: 'Erro' },
					{ color: '#fa987f', label: 'Reserva lenta (>10s)' },
					{ color: '#fae38e', label: 'Sem reserva / sem atualização (>3min)' },
				].map(({ color, label }) => (
					<Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 0.5, opacity: 0.85 }} />
						<Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>{label}</Typography>
					</Box>
				))}
			</Box>

			{/* Countdown */}
			<Box sx={{ textAlign: 'center' }}>
				<Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.3 }}>
					próxima atualização
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
					<Box sx={{ width: 80, height: 3, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
						<Box
							sx={{
								height: '100%',
								width: `${(countdown / TV_REFRESH_INTERVAL) * 100}%`,
								bgcolor: countdown <= 5 ? orange[400] : '#7869DF',
								borderRadius: 99,
								transition: 'width 1s linear',
							}}
						/>
					</Box>
					<Typography
						sx={{
							color: countdown <= 5 ? orange[400] : 'rgba(255,255,255,0.4)',
							fontWeight: 600,
							fontSize: '0.9rem',
							fontVariantNumeric: 'tabular-nums',
							minWidth: 28,
						}}
					>
						{countdown}s
					</Typography>
				</Box>
			</Box>

			{/* Right: clock + exit */}
			<Box display="flex" alignItems="center" gap={2}>
				<Typography
					sx={{
						color: 'white',
						fontWeight: 300,
						fontSize: '1.6rem',
						fontVariantNumeric: 'tabular-nums',
						letterSpacing: '0.04em',
						lineHeight: 1,
					}}
				>
					{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
				</Typography>
				<Tooltip title="Sair do Modo TV (ESC)">
					<IconButton
						onClick={onExit}
						sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
					>
						<FullscreenExitIcon />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
	);
};

// ─── TvSummaryCards ───────────────────────────────────────────
const TvSummaryCards = ({ summaryData }) => {
	if (!summaryData || summaryData.length === 0) return null;
	const total = summaryData.reduce((acc, s) => acc + (s.count || 0), 0);

	return (
		<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
			<Box
				sx={{
					bgcolor: 'rgba(255,255,255,0.06)',
					borderRadius: 2,
					px: 2,
					py: 1,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					minWidth: 70,
				}}
			>
				<Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
					Total
				</Typography>
				<Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
					{total}
				</Typography>
			</Box>
			{summaryData.map((s) => {
				const label = statusChoices.find((c) => c.id === s.id)?.name || s.id;
				const color = TV_STATUS_COLORS[s.id] || '#9e9e9e';
				return (
					<Box
						key={s.id}
						sx={{
							bgcolor: `${color}18`,
							border: `1px solid ${color}40`,
							borderRadius: 2,
							px: 2,
							py: 1,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							minWidth: 70,
						}}
					>
						<Typography sx={{ color: `${color}cc`, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3, textAlign: 'center' }}>
							{label}
						</Typography>
						<Typography sx={{ color, fontWeight: 700, fontSize: '1.5rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
							{s.count}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};

// ─── TvFillinsTable ───────────────────────────────────────────
const headCellSx = {
	color: 'rgba(255,255,255,0.4)',
	fontSize: '0.65rem',
	textTransform: 'uppercase',
	letterSpacing: '0.08em',
	fontWeight: 600,
	py: 1,
	px: 1.5,
	bgcolor: '#1a1d2e',
	borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const cellSx = {
	color: 'rgba(255,255,255,0.85)',
	fontSize: '0.78rem',
	py: 0.9,
	px: 1.5,
	borderBottom: '1px solid rgba(255,255,255,0.05)',
};

const TV_COLUMNS = ['Status', 'Posto', 'Rede', 'Combustível', 'Frentista', 'Valor', 'Volume', 'Tempo', 'Reserva'];

const TvFillinsTable = ({ data, tick }) => {
	if (!data || data.length === 0) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
				<Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.1rem' }}>
					Nenhum abastecimento em andamento
				</Typography>
			</Box>
		);
	}

	return (
		<Table stickyHeader size="small">
			<TableHead>
				<TableRow>
					{TV_COLUMNS.map((h) => (
						<TableCell key={h} sx={headCellSx}>{h}</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{data.map((record, index) => {
					const statusLabel = statusChoices.find((s) => s.id === record.status)?.name || record.status || '-';
					const statusColor = TV_STATUS_COLORS[record.status] || '#9e9e9e';
					const rowBg = getTvRowBg(record);
					const reserveSecs = record.reservedAt
						? ((new Date(record.reservedAt) - new Date(record.startedAt)) / 1000).toFixed(1)
						: null;

					return (
						<RecordContextProvider key={record.id || record.reserveId || index} value={record}>
							<TableRow
								sx={{
									bgcolor: rowBg,
									'&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
									transition: 'background-color 0.3s',
								}}
							>
								<TableCell sx={cellSx}>
									<Chip
										label={statusLabel}
										size="small"
										sx={{
											bgcolor: `${statusColor}22`,
											color: statusColor,
											fontSize: '0.65rem',
											height: 20,
											fontWeight: 600,
											border: `1px solid ${statusColor}50`,
											'& .MuiChip-label': { px: 1 },
										}}
									/>
								</TableCell>
								<TableCell sx={cellSx}>
									<ReferenceField source="stationId" reference="stations" link={false} emptyText="-">
										<TextField source="name" />
									</ReferenceField>
								</TableCell>
								<TableCell sx={cellSx}>
									<ReferenceField source="chainId" reference="chains" link={false} emptyText="-">
										<TextField source="name" />
									</ReferenceField>
								</TableCell>
								<TableCell sx={cellSx}>
									<FuelField />
								</TableCell>
								<TableCell sx={cellSx}>
									<ReferenceField source="attendant.id" reference="attendants" link={false} emptyText="-">
										<FunctionField render={(rec) => formatAttendantLabel(rec)} />
									</ReferenceField>
								</TableCell>
								<TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
									<ValueField />
								</TableCell>
								<TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
									<VolumeField />
								</TableCell>
								<TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.55)' }}>
									{/* tick forces re-render every second for live elapsed time */}
									{tick >= 0 && formatElapsed(record.startedAt)}
								</TableCell>
								<TableCell
									sx={{
										...cellSx,
										fontVariantNumeric: 'tabular-nums',
										color: reserveSecs !== null && parseFloat(reserveSecs) > 10
											? '#EF5350'
											: 'rgba(255,255,255,0.55)',
									}}
								>
									{reserveSecs !== null ? `${reserveSecs}s` : '-'}
								</TableCell>
							</TableRow>
						</RecordContextProvider>
					);
				})}
			</TableBody>
		</Table>
	);
};

// ─── FillinsList ──────────────────────────────────────────────
const FillinsList = () => {
	const listProps = useListContext();
	const { isTvMode, countdown, tick, refreshTrigger, onExit } = useContext(TvModeContext);
	const scrollRef = useRef(null);
	const filterValuesRef = useRef(listProps.filterValues);
	filterValuesRef.current = listProps.filterValues;

	// Atualiza filtros de data a cada refresh do modo TV (janela rolante: sempre 2h atrás até fim do dia atual)
	useEffect(() => {
		if (!isTvMode || refreshTrigger === 0) return;
		listProps.setFilters({
			...filterValuesRef.current,
			from: moment().add(-2, 'hours').toISOString(),
			to: moment().endOf('day').toISOString(),
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTrigger]);

	let data = [];
	let summaryData = [];

	if (listProps.data) {
		const fillinInfo = listProps.data[0];
		if (fillinInfo) {
			data = fillinInfo.data || [];
			summaryData = fillinInfo.summaryData || [];
		}
	}

	// Auto-scroll in TV mode
	useEffect(() => {
		if (!isTvMode) return;
		let paused = true;
		let pauseTimeout = null;

		pauseTimeout = setTimeout(() => { paused = false; }, 5000);

		const interval = setInterval(() => {
			const el = scrollRef.current;
			if (!el || paused) return;
			const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
			if (atBottom && el.scrollHeight > el.clientHeight) {
				paused = true;
				pauseTimeout = setTimeout(() => {
					if (el) el.scrollTop = 0;
					pauseTimeout = setTimeout(() => { paused = false; }, 3000);
				}, 3000);
			} else {
				el.scrollTop += 1;
			}
		}, 60);

		return () => {
			clearInterval(interval);
			if (pauseTimeout) clearTimeout(pauseTimeout);
		};
	}, [isTvMode]);

	if (isTvMode) {
		return createPortal(
			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					zIndex: 9999,
					bgcolor: '#0d0f18',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<TvHeader onExit={onExit} countdown={countdown} />
				<Box
					ref={scrollRef}
					sx={{
						flex: 1,
						overflow: 'auto',
						p: 2,
						'&::-webkit-scrollbar': { width: 6 },
						'&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
						'&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 99 },
					}}
				>
					<TvSummaryCards summaryData={summaryData} />
					<TvFillinsTable data={data} tick={tick} />
				</Box>
			</Box>,
			document.body,
		);
	}

	return (
		<>
			<Typography sx={titleSx}>Total</Typography>
			<Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={1}>
				<SelectField source="id" choices={statusChoices} emptyText='Todos' label="Status" />
				<NumberField source="count" label="Total" textAlign="center" />
			</Datagrid>

			<Typography sx={titleSx}>Abastecimentos em Andamento</Typography>
			<CustomizableDatagrid
				rowStyle={slowReserve}
				resource={listProps.resource}
				bulkActionButtons={false}
				data={data}
				defaultColumns={['stationId', 'chainId', 'status', 'reserveId', 'value', 'amount', 'fuel_id', 'startedAt']}
			>
				<ReferenceField label="Rede" emptyText="Rede não identificada" source="chainId" reference="chains" basePath="chains" link="show">
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField label="Posto" emptyText="Posto não identificado" source="stationId" reference="stations" basePath="stations" link="show">
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField label="Frentista" emptyText="Não identificado" source="attendant.id" reference="attendants" basePath="attendants" link="show">
					<FunctionField render={record => formatAttendantLabel(record)} />
				</ReferenceField>
				<TextField source="reserveId" label="Reserva" />
				<ReferenceField label="Cliente" emptyText="Não identificado" source="customerId" reference="customers" basePath="customers" link="show">
					<TextField source="name" />
				</ReferenceField>
				<SelectField emptyText="Não identificado" source="status" choices={statusChoices} />
				<ValueField label="Valor" />
				<VolumeField label="Volume" />
				<PriceField label="Preço" />
				<FuelField label="Combustível" />
				<DateField source="startedAt" label="Criado em" showTime locales="pt-BR" />
				<FunctionField source="reservedAt" label="Reservado em (segundos)" render={record => record.reservedAt ? ((new Date(record.reservedAt) - new Date(record.startedAt)) / 1000).toFixed(1) : '-'} />
				<DateField source="reservedAt" label="Reservado em" showTime locales="pt-BR" />
				<DateField source="updatedAt" label="Atualizado em" showTime locales="pt-BR" />
			</CustomizableDatagrid>
		</>
	);
};

// ─── fillinsExporter (unchanged) ─────────────────────────────
const fillinsExporter = async (data) => {
	return data[0].data.map(fillin => ({
		status: fillin.status,
		reserveId: fillin.reserveId,
		value: fillin.value || (fillin.fillInInfo && fillin.fillInInfo.value),
		volume: fillin.volume || (fillin.fillInInfo && fillin.fillInInfo.volume),
		startedAt: fillin.startedAt,
	}));
};

const HoseNumberChip = () => {
	const record = useRecordContext();
	if (!record) return null;
	return <ChipField record={{ hoseNumber: record }} source="hoseNumber" />;
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
			<ReferenceArrayField label="Posto" source="stationIds" textAlign="center" basePath="stations" reference="stations" link={false}>
				<Datagrid bulkActionButtons={false}>
					<TextField source="name" label="Postos" />
				</Datagrid>
			</ReferenceArrayField>
			<ReferenceField label="Combustível" emptyText="Todos" source="fuelId" textAlign="center" basePath="fuels" reference="fuels" link={false}>
				<TextField source="name" />
			</ReferenceField>
			<ReferenceArrayField label="Grupo de clientes" emptyText="Todos" source="customerGroupIds" textAlign="center" basePath="chains/chainId/customer-groups" reference="chains/chainId/customer-groups" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<ArrayField label="Bicos" emptyText="Todos" source="hoseNumbers" textAlign="center">
				<SingleFieldList>
					<HoseNumberChip />
				</SingleFieldList>
			</ArrayField>
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

// ─── FillinsActions ───────────────────────────────────────────
const FillinsActions = ({ reportRef, onEnterTvMode }) => (
	<TopToolbar sx={{ alignItems: 'center' }}>
		<CustomExportButton preExport={fillinsExporter} maxResults={50000} />
		<PrintButton printRef={reportRef} sx={{ alignSelf: 'center', height: '100%', verticalAlign: 'center' }} />
		<Tooltip title="Modo TV (tela cheia)">
			<IconButton onClick={onEnterTvMode} size="small" sx={{ ml: 0.5, color: 'rgba(0,0,0,0.54)', '&:hover': { color: 'primary.main' } }}>
				<TvIcon fontSize="small" />
			</IconButton>
		</Tooltip>
	</TopToolbar>
);

// ─── ReportCurrentFillins ─────────────────────────────────────
const ReportCurrentFillins = (props) => {
	const translate = useTranslate();
	const [isTvMode, setIsTvMode] = useState(false);
	const [countdown, setCountdown] = useState(TV_REFRESH_INTERVAL);
	const [tick, setTick] = useState(0);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	// Auto-refresh countdown in TV mode (dispara refreshTrigger que atualiza filtros de data no FillinsList)
	useEffect(() => {
		if (!isTvMode) return;
		setCountdown(TV_REFRESH_INTERVAL);
		const interval = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					setRefreshTrigger((n) => n + 1);
					return TV_REFRESH_INTERVAL;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(interval);
	}, [isTvMode]);

	// Tick every second in TV mode (drives elapsed time display)
	useEffect(() => {
		if (!isTvMode) return;
		const t = setInterval(() => setTick((n) => n + 1), 1000);
		return () => clearInterval(t);
	}, [isTvMode]);

	// Sync with native fullscreen exit (ESC key)
	useEffect(() => {
		const onFsChange = () => { if (!document.fullscreenElement) setIsTvMode(false); };
		document.addEventListener('fullscreenchange', onFsChange);
		return () => document.removeEventListener('fullscreenchange', onFsChange);
	}, []);

	const handleEnterTvMode = () => {
		document.documentElement.requestFullscreen().catch(() => {});
		setIsTvMode(true);
	};

	const handleExitTvMode = () => {
		if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
		setIsTvMode(false);
	};

	const placeId = getPlaceId();

	const listFilters = [
		<BetweenDatesTimesInput alwaysOn />,
		<ReferenceArrayInput
			source="stationIds"
			reference="stations"
			perPage={null}
			sort={{ field: 'name', order: 'ASC' }}
			alwaysOn
		>
			<AutocompleteArrayInput
				filterToQuery={filterToQuery}
				emptyText="Todos"
				label="Posto"
				sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
				optionText="name"
				noOptionsText={translate('ra.input.autocomplete.no_options_text')}
			/>
		</ReferenceArrayInput>,
		<ReferenceArrayInput
			source="fuelIds"
			reference="fuels"
			sort={{ field: 'name', order: 'ASC' }}
			alwaysOn
		>
			<SelectArrayInput
				label="Combustível"
				emptyText="Todos"
				style={{ minWidth: 180 }}
				optionText="name"
			/>
		</ReferenceArrayInput>,
		<SelectInput label="Desconto" choices={discountChoices} emptyText="Todos" source="discount" alwaysOn />,
		<SelectInput label="Status" choices={statusChoices} emptyText="Todos" source="status" alwaysOn />,
	];

	const stationsIndex = listFilters.findIndex(({ props: p }) => p.reference === 'stations');

	if (placeId && stationsIndex > -1) {
		listFilters.splice(stationsIndex, 1);
	}

	if (stationsIndex > -1) {
		listFilters.splice(stationsIndex, 0,
			<ReferenceInput
				source="chainId"
				reference="chains"
				perPage={null}
				sort={{ field: 'name', order: 'ASC' }}
				alwaysOn
			>
				<AutocompleteInput
					filterToQuery={filterToQuery}
					label="Rede"
					style={{ minWidth: 180 }}
					optionText="name"
				/>
			</ReferenceInput>
		);
	}

	return (
		<TvModeContext.Provider value={{ isTvMode, countdown, tick, refreshTrigger, onExit: handleExitTvMode }}>
			<Permission permission={PERMISSIONS.ADMIN}>
				<ReportList periodRequired
					{...props}
					basePath="fillins"
					title="Abastecimento"
					resource="reports/current-fillins"
					filters={listFilters}
					sort={{ field: 'created_at', order: 'DESC' }}
					filterDefaultValues={{
						stationIds: placeId ? [placeId] : null,
						chainId: getChainId(),
						from: moment().add(-2, 'hours').toISOString(),
						to: moment().endOf('day').toISOString(),
					}}
					showPagination={false}
					bulkActionButtons={false}
					titleOnPrint="Relatório de abastecimento em andamento"
					filterTablePrint={<FilterTablePrint />}
					landscape
					actions={<FillinsActions onEnterTvMode={handleEnterTvMode} />}
				>
					<FillinsList />
				</ReportList>
			</Permission>
		</TvModeContext.Provider>
	);
};

export default ReportCurrentFillins;
